import { Box, IconButton } from "@mui/material";
import DataTable from "app/components/DataTable";
import { DownloadIcon } from "app/icons";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Financial, FinancialStatus } from "../../../types";
import { useStyles } from "./styles";

type Props = {
  rows: Financial[];
};

const FinancialTable = ({ rows }: Props) => {
  const { t, i18n } = useTranslation();
  const styles = useStyles();

  const getStatusClassName = (status: FinancialStatus) => {
    switch (status) {
      case "PAID":
        return `${styles.statusChip} ${styles.paidChip}`;
      case "SETTLED":
        return `${styles.statusChip} ${styles.settledChip}`;
      case "OVERDUE":
        return `${styles.statusChip} ${styles.overdueChip}`;
      default:
        return styles.statusChip;
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "Download",
        label: "",
        minWidth: 90,
        align: "right",
        format: () => (
          <IconButton
            className={styles.downloadButton}
            aria-label={t("CUSTOMERS.DETAILS.FINANCIAL.TABLE.DOWNLOAD")}
          >
            <DownloadIcon width={24} height={24} />
          </IconButton>
        ),
      },
      {
        id: "FinancialStatus",
        label: t("CUSTOMERS.DETAILS.FINANCIAL.TABLE.STATUS"),
        minWidth: 100,
        align: "right",
        format: (row: Financial) => (
          <span className={getStatusClassName(row.FinancialStatus)}>
            {t(`CUSTOMERS.DETAILS.FINANCIAL.STATUS.${row.FinancialStatus}`)}
          </span>
        ),
      },
      {
        id: "SumUp",
        label: t("CUSTOMERS.DETAILS.FINANCIAL.TABLE.SUM_UP"),
        minWidth: 90,
        align: "right",
        format: (row: Financial) => `₪${row.SumUp}`,
      },
      { 
        id: "Description",
        label: t("CUSTOMERS.DETAILS.FINANCIAL.TABLE.DESCRIPTION"),
        minWidth: 140,
        align: "right"
      },
      { 
        id: "Type",
        label: t("CUSTOMERS.DETAILS.FINANCIAL.TABLE.TYPE"),
        minWidth: 200,
        align: "right",
        format: (row: Financial) => t(`CUSTOMERS.DETAILS.FINANCIAL.TYPE.${row.Type}`),
      },
      {
        id: "DocumentNumber",
        label: t("CUSTOMERS.DETAILS.FINANCIAL.TABLE.DOCUMENT_NUMBER"),
        minWidth: 110,
        align: "right",
      },
      {
        id: "Date",
        label: t("CUSTOMERS.DETAILS.FINANCIAL.TABLE.DATE"),
        minWidth: 100,
        align: "right",
      },
    ],
    [styles, t]
  );

  return (
    <Box className={styles.root} dir={i18n.dir()}>
      <DataTable
        rows={rows}
        columns={columns}
        serialEnabled={false}
        noDataLabel={t("CUSTOMERS.DETAILS.FINANCIAL.EMPTY")}
      />
    </Box>
  );
};

export default FinancialTable;
